import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Icons from "../../utils/components/icons";
import DropDown from "../../utils/components/common/dropdown/dropdown";
import { useTranslation } from "react-i18next";
import {
  getCurrentSiteLocalData,
  scheduleKeys,
  fleetStatus,
  scheduleLabel,
  updateLocalStorageData,
  schdState,
} from "../../utils/constants/scheduleConstants";
import EmptyScreen from "../../utils/components/common/EmptyScreen/EmptyScreen";
import {
  getDevicesForFleetDashboard,
  getFleetScheduleOperationDetails,
  performFleetSchedule,
} from "../../services/SchedulerService";
import { OrganisationContext } from "../../layouts/MainLayout";
import ModalComponent from "../../utils/components/common/modalComponent/ModalComponent";
import moment from "moment";
import BarSpinner from "../../utils/components/common/BarSpinner/BarSpinner";
import ScheduleSettingEdit from "../deviceView/ScheduleSettingEdit";
import "../deviceView/ScheduleSetting.scss";

const init = {
  open: false,
  msg: "",
  type: "",
  data: {},
};

const FleetSchedule = (props) => {
  const { setLoading, clickedTemplateData, resetCB } = props;
  const { t } = useTranslation("schedules");
  const { siteData } = useContext(OrganisationContext);

  const [schdAction, setSchdAction] = useState("");
  const [allDevices, setAllDevices] = useState([]);
  const [updatedDevicesList, setUpdatedDevicesList] = useState([]);
  const [processDeviceList, setProcessDeviceList] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [taskStatus, setTaskStatus] = useState(schdState.disable);
  const [isEdit, setIsEdit] = useState(false);

  const [confirmModal, setConfirmModal] = useState({ ...init });
  const [infoModal, setInfoModal] = useState({
    open: false,
    msg: "",
    translated: false,
  });

  const refreshSchdStatusApiRef = useRef();

  // Action dropdown list
  const scheudleDropdownList = [
    {
      label: scheduleLabel.saveToDevice,
      value: scheduleKeys.saveToDevice,
      tooltip: t(`fleetSchedule.dropdownTooltip.saveToDevice`),
    },
    {
      label: scheduleLabel.removeFromDevice,
      value: scheduleKeys.removeFromDevice,
      tooltip: t(`fleetSchedule.dropdownTooltip.removeFromDevice`),
    },
    {
      label: scheduleLabel.enableSchedule,
      value: scheduleKeys.enableSchedule,
      tooltip: t(`fleetSchedule.dropdownTooltip.enableSchedule`),
    },
    {
      label: scheduleLabel.disableSchedule,
      value: scheduleKeys.disableSchedule,
      tooltip: t(`fleetSchedule.dropdownTooltip.disableSchedule`),
    },
  ];

  // Icons and Title object for devices
  const iconsAndTitle = {
    [fleetStatus.Completed]: {
      icon: Icons.greenCheckIcon,
      title: t("fleetSchedule.iconsTooltip.completed"),
    },
    [fleetStatus.Failed]: {
      icon: Icons.failedIcon,
      title: t("fleetSchedule.iconsTooltip.failed"),
    },
    [fleetStatus.Timeout]: {
      icon: Icons.timeoutIcon,
      title: t("fleetSchedule.iconsTooltip.timeout"),
    },
    [fleetStatus.Disabled]: {
      icon: Icons.disabledCheckboxIcon,
      title: t("fleetSchedule.iconsTooltip.disabled"),
    },
    [fleetStatus.NA]: {
      icon: Icons.disabledCheckboxIcon,
      title: t("fleetSchedule.iconsTooltip.disabled"),
    },
    [fleetStatus.InProgress]: {
      icon: <BarSpinner />, //<img src={processIcon} alt="processIcon" />,
      title: t("fleetSchedule.iconsTooltip.inProgress"),
    },
  };

  // Add Ellipsis on text
  const textEllipsis = (text, maxLength) => {
    if (!text) return "-";
    if (text && text.length > maxLength) {
      return text.substring(0, maxLength) + "...";
    }
    return text;
  };

  // Make text in lower case
  const getTxtInLower = useCallback((schdStatus) => {
    return schdStatus ? schdStatus.toLowerCase() : "";
  }, []);

  // Fleet schedule status api function
  const getFleetSchdStatusAPI = useCallback(async () => {
    try {
      const localData = getCurrentSiteLocalData(siteData.id);
      let queryParams = {
        fleetId: localData.fleetId,
      };
      let res = await getFleetScheduleOperationDetails(queryParams);
      let data = res?.data;
      updateLocalStorageData(
        {
          processedList: data.devices,
          status: data.fleetStatus,
        },
        siteData.id
      );

      setProcessDeviceList(data.devices);
    } catch (err) {
      setInfoModal((prev) => ({
        ...prev,
        open: true,
        msg: t("fleetSchedule.fleetErrorMessage"),
      }));
    }
  }, [siteData.id, t]);

  // Perform fleet schedule api on devices clicked on submit action button
  const performFleetScheduleAPI = useCallback(async () => {
    try {
      const localData = getCurrentSiteLocalData(siteData.id);
      setLoading(true);
      let status =
        schdAction === scheduleKeys.saveToDevice
          ? taskStatus === schdState.enable
            ? "enable"
            : "disable"
          : schdAction === scheduleKeys.enableSchedule
          ? "enable"
          : schdAction === scheduleKeys.disableSchedule
          ? "disable"
          : "remove";

      let jobList =
        schdAction === scheduleKeys.saveToDevice
          ? [...(localData?.templateData?.jobList || [])]
          : "";

      let queryParams = {
        siteId: siteData.id,
        scheduleType: "CHANGE_MODE",
        scheduleStatus: status,
        ...(jobList && jobList.length && { jobList: jobList }),
        deviceList: [...localData?.deviceListIds],
        userTimezone: moment.tz.guess(),
      };

      let res = await performFleetSchedule(queryParams);

      let data = res?.data || {};
      if (data?.Message === "Success") {
        let tempDevices = [];
        allDevices?.map((item) => {
          if (localData?.deviceListIds.includes(item.deviceId)) {
            tempDevices.push({
              deviceId: item.deviceId,
              deviceName: item.deviceName,
              deviceScheduleStatus: fleetStatus.Disabled,
              deviceFleetStatus: fleetStatus.Disabled,
            });
          }
          return item;
        });

        updateLocalStorageData(
          {
            fleetId: data?.FleetId,
            processedList: tempDevices,
            status: fleetStatus.InProgress,
          },
          siteData.id
        );

        setProcessDeviceList(tempDevices);
        getFleetSchdStatusAPI();
      }
      setLoading(false);
    } catch (err) {
      setInfoModal((prev) => ({
        ...prev,
        open: true,
        msg: t("fleetSchedule.fleetErrorMessage"),
      }));
      setLoading(false);
    }
  }, [
    allDevices,
    getFleetSchdStatusAPI,
    schdAction,
    setLoading,
    siteData.id,
    t,
    taskStatus,
  ]);

  // First time to check local storage and update
  useEffect(() => {
    updateLocalStorageData({}, siteData.id);
    let currData = getCurrentSiteLocalData(siteData.id);
    setSchdAction(currData?.action || "");
    setTasks(currData?.templateData ? currData?.templateData?.jobList : []);
    setProcessDeviceList(currData?.processedList || []);
    setTaskStatus(currData?.schdState || schdState.disable);
    if (currData?.status === fleetStatus.InProgress) {
      getFleetSchdStatusAPI();
    }
    return () => {
      clearInterval(refreshSchdStatusApiRef?.current);
    };
  }, [getFleetSchdStatusAPI, siteData.id]);

  // Get Devices from API
  const getDevices = useCallback(async () => {
    try {
      setLoading(true);
      let queryParams = {
        siteId: siteData.id,
      };
      let res = await getDevicesForFleetDashboard(queryParams);
      setAllDevices(res.data.items || []);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setAllDevices([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteData.id]);

  // Device list API calling
  useEffect(() => {
    getDevices();
  }, [getDevices, clickedTemplateData]);

  // Update states when click on fleet schedule from 3 dots action popup
  useEffect(() => {
    if (clickedTemplateData) {
      setSchdAction(scheduleKeys.saveToDevice);
      setTasks(clickedTemplateData.jobList || []);
      setTaskStatus(schdState.enable);

      updateLocalStorageData(
        {
          action: scheduleKeys.saveToDevice,
          deviceListIds: [],
          templateData: { jobList: clickedTemplateData.jobList },
        },
        siteData.id
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clickedTemplateData]);

  // Update checkbox status
  const updateItems = useCallback(
    (deviceListItem) => {
      let checkedIds =
        getCurrentSiteLocalData(siteData.id)?.deviceListIds || [];

      deviceListItem.forEach(
        (item) =>
          (item.isChecked = checkedIds.includes(item.deviceId) ? true : false)
      );

      setUpdatedDevicesList(deviceListItem);
    },
    [siteData.id]
  );

  // Filter device list according to schedule action's
  const devicesList = useMemo(() => {
    let tempDevices = [];
    if (schdAction === scheduleKeys.saveToDevice) {
      tempDevices = [...allDevices];
    }

    if (schdAction === scheduleKeys.removeFromDevice) {
      tempDevices = allDevices.filter((device) =>
        ["disabled", "enabled"].includes(getTxtInLower(device?.scheduleStatus))
      );
    }

    if (schdAction === scheduleKeys.enableSchedule) {
      tempDevices = allDevices.filter((device) =>
        ["disabled"].includes(getTxtInLower(device?.scheduleStatus))
      );
    }

    if (schdAction === scheduleKeys.disableSchedule) {
      tempDevices = allDevices.filter((device) =>
        ["enabled"].includes(getTxtInLower(device?.scheduleStatus))
      );
    }
    updateItems(tempDevices);
    return [...tempDevices];
  }, [allDevices, getTxtInLower, schdAction, updateItems]);

  // Checkbox handle
  const updateCheckbox = useCallback(
    (idx) => {
      let temp = [...updatedDevicesList];
      temp[idx].isChecked = !temp[idx].isChecked;
      setUpdatedDevicesList([...temp]);

      // Update device list ids in local storage
      let deviceId = temp[idx].deviceId;
      let checkedList = getCurrentSiteLocalData(siteData.id)?.deviceListIds;
      let isInclude = checkedList.includes(deviceId);
      let deviceIdx = checkedList.indexOf(deviceId);
      let isChecked = temp[idx].isChecked;

      if (isChecked && !isInclude) checkedList.push(deviceId);

      if (!isChecked && isInclude && deviceIdx > -1)
        checkedList.splice(deviceIdx, 1);

      updateLocalStorageData({ deviceListIds: checkedList }, siteData.id);
    },
    [siteData.id, updatedDevicesList]
  );

  // Select all or clear all
  const selectOrClearAllItem = useCallback(
    (flag) => {
      let deviceIds = [];
      let tempList = [...updatedDevicesList];

      tempList.forEach((ele) => {
        ele.isChecked = flag;
        flag && ele.mode !== "OFFLINE" && deviceIds.push(ele.deviceId);
      });
      setUpdatedDevicesList([...tempList]);

      updateLocalStorageData({ deviceListIds: deviceIds }, siteData.id);
    },
    [siteData.id, updatedDevicesList]
  );

  // Check all item selected or not
  const isAllItemSelected = useMemo(() => {
    let filterList = updatedDevicesList.length
      ? updatedDevicesList.filter((item) => item.mode !== "OFFLINE")
      : [];
    return filterList.length
      ? filterList.every((item) => item.isChecked === true)
      : false;
  }, [updatedDevicesList]);

  // Total count of selected devices
  const itemSelectedCount = useMemo(() => {
    return updatedDevicesList.filter(
      (item) => item.mode !== "OFFLINE" && item.isChecked === true
    ).length;
  }, [updatedDevicesList]);

  // Action buttion is enable or disable
  const isSubmitBtnEnable = useMemo(() => {
    // task need to changes in futrue for now is static
    if (schdAction === scheduleKeys.saveToDevice) {
      return !tasks?.length || !itemSelectedCount ? false : true;
    } else {
      return !itemSelectedCount ? false : true;
    }
  }, [itemSelectedCount, schdAction, tasks?.length]);

  // Confirmation modal close
  const onConfirmModalClose = useCallback(() => {
    //Reset on modal close
    setConfirmModal((prev) => ({
      ...prev,
      ...init,
    }));
  }, []);

  // Reset states and localStorage
  const resetStates = useCallback(() => {
    setSchdAction("");
    resetCB();
    setTasks([]);
    setTaskStatus(schdState.disable);

    updateLocalStorageData(
      {
        deviceListIds: [],
        action: "",
        fleetId: "",
        templateData: {},
        processedList: [],
        status: fleetStatus.notInitiate,
        schdState: schdState.disable
      },
      siteData.id
    );
    getDevices();
  }, [getDevices, resetCB, siteData.id]);

  // Confirmation from confirm modal
  const onConfirm = useCallback(
    async (type) => {
      if (type === "resetFleet") {
        setConfirmModal((prev) => ({
          ...prev,
          open: false,
          msg: "",
          type: "",
          data: {},
        }));
        resetStates();
      } else if (type === scheduleKeys.removeFromDevice) {
        performFleetScheduleAPI();
      }
    },
    [performFleetScheduleAPI, resetStates]
  );

  // Action button handler
  const actionHandle = useCallback(() => {
    if (schdAction === scheduleKeys.removeFromDevice) {
      setConfirmModal((prev) => ({
        ...prev,
        open: true,
        msg: t("fleetSchedule.schdRemoveMessage"),
        type: scheduleKeys.removeFromDevice,
      }));
      return;
    }
    performFleetScheduleAPI();
  }, [performFleetScheduleAPI, schdAction, t]);

  const currentLocalData = getCurrentSiteLocalData(siteData.id);

  // Fleet status API call every 10 sec
  useEffect(() => {
    clearInterval(refreshSchdStatusApiRef?.current);
    if (currentLocalData.status === fleetStatus.InProgress) {
      refreshSchdStatusApiRef.current = setInterval(() => {
        getFleetSchdStatusAPI();
      }, 10000);
    } else {
      clearInterval(refreshSchdStatusApiRef?.current);
    }
  }, [currentLocalData.status, getFleetSchdStatusAPI, siteData.id]);

  // Close Info modal
  const onInfoModalClose = useCallback(() => {
    setInfoModal((prev) => ({ ...prev, open: false, msg: "" }));
  }, []);

  const isFleetInitiated = currentLocalData?.status !== fleetStatus.notInitiate;
  const isFleetComplete = currentLocalData?.status === fleetStatus.Completed;
  const isFleetInProgress = currentLocalData?.status === fleetStatus.InProgress;

  return (
    <>
      <div
        className={`fleet-schedule-section 
        ${schdAction ? schdAction : "noScheduleAction"} 
        ${confirmModal.open || isEdit ? "isModalOpen" : ""} 
        ${isFleetInitiated ? "isInitiated" : ""}
        ${devicesList.length ? "" : "noDevices"}
        `}
      >
        {/* Header Start */}
        <div className="rightHeader">
          <h1 className="heading-h1 fleet-color" id="fleet-title">Fleet Schedule</h1>
          {!isFleetInitiated && schdAction && (
            <button
              type="button"
              className="roundBtn primary"
              id="reset_button"
              onClick={() => {
                setConfirmModal((prev) => ({
                  ...prev,
                  open: true,
                  msg: t("fleetSchedule.resetFleetMessage"),
                  type: "resetFleet",
                }));
              }}
              title={t("fleetSchedule.reset")}
            >
              {Icons.reset}
            </button>
          )}
        </div>
        {/* Header End */}

        {/* Schedule dropdown Start */}
        <div className={`action-section ${schdAction ? "" : "is-placeholder"}`}>
          <DropDown
            autoid="schdActionDropdown"
            disabled={isFleetInitiated}
            title={
              schdAction ? t(`fleetSchedule.dropdownTooltip.${schdAction}`) : ""
            }
            fleetActionPlaceholder
            options={scheudleDropdownList}
            value={schdAction}
            onSelection={(val) => {
              if (val !== schdAction) {
                getDevices();
              }
              setSchdAction(val);

              // Update schedule action in local storge
              updateLocalStorageData(
                { deviceListIds: [], action: val },
                siteData.id
              );
            }}
            showListTooltip={true}
            showIconOnDisable={true}
            alwaysShowIcon={true}
          />
        </div>
        {/* Schedule dropdown End */}

        {/* Edit task section start */}
        {schdAction && schdAction === scheduleKeys.saveToDevice && (
          <div className="schedule-status-section">
            <div>
              <div className="lable-value-text pb-1">
                <span className="label text-h2" id="task-label">
                  {t("fleetSchedule.tasks")} :&nbsp;
                </span>
                <span className="value text-h2" id="task-val">
                  {tasks ? tasks?.length : 0}/6
                </span>
              </div>
              <div className="lable-value-text">
                <span className="label text-h2" id="schd-status-title">
                  {t("fleetSchedule.scheduleState")} :&nbsp;
                </span>
                <span className="value text-h2" id="schd-status-val">{taskStatus}</span>
              </div>
            </div>
            <div>
              <button
                id="editBtn"
                className="roundBtn primary"
                disabled={isFleetInitiated}
                onClick={() => {
                  setIsEdit(true);
                }}
              >
                <span>
                  {t("fleetSchedule.edit")}{" "}
                  <span>{Icons.deviceSchedulerIsEnabledIcon}</span>
                </span>
              </button>
            </div>
          </div>
        )}
        {/* Edit task section end */}

        {/* Device section start */}
        <div className="schedule-device-section">
          <div className="device-text" id="device-text">
            <span className="label text-h2" s>
              {t("fleetSchedule.devices")} :{" "}
              {isFleetInitiated
                ? processDeviceList?.length
                : devicesList.length}{" "}
              <span
                title={
                  isFleetInitiated
                    ? t("fleetSchedule.iIconMessage")
                    : schdAction
                    ? t(`fleetSchedule.${schdAction}`)
                    : ""
                }
                id="info-icon"
              >
                {Icons.iIcon}
              </span>
            </span>
          </div>
          <div
            className="select-all-div"
            onClick={() => {
              if (!isFleetInitiated) {
                selectOrClearAllItem(!isAllItemSelected);
              }
            }}
          >
            <div className="text-h2 actionAllBtn" id="actionAllBtn">
              {isAllItemSelected
                ? t("fleetSchedule.clearAll")
                : t("fleetSchedule.selectAll")}
            </div>
            <div className="text-h4 actionAllValue" id="actionAllValue">
              (
              {isFleetInitiated ? processDeviceList?.length : itemSelectedCount}
              /
              {isFleetInitiated
                ? processDeviceList?.length
                : devicesList.length}
              )
            </div>
          </div>
        </div>

        {/* Before initiated start*/}
        {schdAction && !isFleetInitiated && (
          <div className="device-listing-section">
            {updatedDevicesList?.length ? (
              updatedDevicesList?.map((item, idx) => {
                let isOffline = item?.mode === "OFFLINE";
                let isSchdIcon = ["disabled", "enabled"].includes(
                  getTxtInLower(item?.scheduleStatus)
                );
                return (
                  <>
                    <div
                      className={`device-item ${
                        isOffline ? "device-offline" : ""
                      }`}
                      key={item.deviceId}
                    >
                      <div className="icon-txt-col">
                        {isOffline ? (
                          <span className="offline-icon" title="Device Offline">
                            {Icons.modeIconOffline}
                          </span>
                        ) : (
                          <span className="schedule-time-icon">
                            {schdAction === scheduleKeys.saveToDevice &&
                            isSchdIcon ? (
                              <span title="Scheduled tasks available on device" id="schedule-time-icon">
                                {Icons.scheduleTimeIcon}
                              </span>
                            ) : (
                              ""
                            )}
                          </span>
                        )}

                        <div
                          className="text-h2 device-name"
                          name="device-name"
                          title={item?.deviceName}
                        >
                          {textEllipsis(item?.deviceName, 25)}
                        </div>
                      </div>
                      {isOffline ? (
                        <div className="checkbox-custom" id="device-checkbox">
                          <span className="blank-box" />
                        </div>
                      ) : (
                        <div
                          className="checkbox-custom"
                          onClick={() => updateCheckbox(idx)}
                        >
                          <span className="blank-box" />
                          {item.isChecked && (
                            <span className="checked-icon">
                              {Icons.checkedCheckboxIcon}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                );
              })
            ) : (
              <div className="empty-screen-box empty-no-device">
                <EmptyScreen
                  small={true}
                  title={t("fleetSchedule.noDevice")}
                  img={Icons.noDeviceOnboarded}
                  desc={t("fleetSchedule.noDeviceDesc")}
                />
              </div>
            )}
          </div>
        )}
        {/* Before initiated end*/}

        {/* After initiated start*/}
        {schdAction && isFleetInitiated && (
          <div
            className={`device-listing-section processing-list ${
              isFleetComplete ? "" : "not-complete"
            }`}
          >
            {processDeviceList?.length ? (
              processDeviceList?.map((item, idx) => {
                let deviceFleetStatus = item.deviceFleetStatus;
                return (
                  <div className={`device-item`} key={item.deviceId}>
                    <div className="icon-txt-col">
                      <div
                        className="text-h2 device-name"
                        title={item?.deviceName}
                      >
                        {textEllipsis(item?.deviceName, 30)}
                      </div>
                    </div>
                    <div className="checkbox-custom">
                      <span
                        className="checked-icon"
                        title={iconsAndTitle[deviceFleetStatus]?.title}
                      >
                        {iconsAndTitle[deviceFleetStatus]?.icon}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="empty-screen-box empty-no-device">
                <EmptyScreen
                  small={true}
                  title={t("fleetSchedule.noDevice")}
                  img={Icons.noDeviceOnboarded}
                  desc={t("fleetSchedule.noDeviceDesc")}
                />
              </div>
            )}
          </div>
        )}
        {/* After initiated end*/}

        {/* Device section end */}

        {/* Submit button section start before initiated */}

        {schdAction && !isFleetInitiated && (
          <div className="submit-btn-section">
            <button
              id="submitBtn"
              className="roundBtn primary"
              disabled={!isSubmitBtnEnable}
              onClick={() => {
                actionHandle();
              }}
            >
              <span>{scheduleLabel[schdAction]} </span>
            </button>

            {schdAction === scheduleKeys.saveToDevice
              ? (!tasks?.length || !itemSelectedCount) && (
                  <span className="label text-h4" id="action-info-msg">
                    {t("fleetSchedule.inlineMessageForSaveToDevice")}
                  </span>
                )
              : !itemSelectedCount && (
                  <span className="label text-h4" id="action-info-msg">
                    {t("fleetSchedule.inlineMessage")}
                  </span>
                )}
          </div>
        )}

        {/* Submit button section end before initiated */}

        {/* Submit button section start After initiated */}

        {schdAction && (isFleetInProgress || isFleetComplete) && (
          <div className="submit-btn-section">
            {isFleetComplete && (
              <div className="complete-btn-section">
                <span>{Icons.doneCheck}</span>
                <span className="label text-h4">
                  {t("fleetSchedule.exeComplete")}
                </span>
                <span className="label text-7">
                  {t("fleetSchedule.clickDone")}
                </span>
              </div>
            )}
            <button
              id="submitBtn"
              className={`roundBtn primary ${
                isFleetInProgress ? "in-progress-btn" : ""
              }`}
              onClick={() => {
                if (isFleetComplete) {
                  resetStates();
                }
              }}
            >
              {isFleetInProgress ? (
                <div className="dot-flashing" />
              ) : (
                <span>{t("fleetSchedule.done")}</span>
              )}
            </button>
          </div>
        )}

        {/* Submit button section end After initiated */}

        {/* No schedule action selected start*/}
        {!schdAction && (
          <div className="empty-screen-box">
            <EmptyScreen
              small={true}
              img={Icons.scheduleIcon}
              desc={t("fleetSchedule.scheduleAction")}
            />
          </div>
        )}
        {/* No schedule action selected end*/}
      </div>

      {/* Comfirmation Modal */}
      <ModalComponent
        idName="schedule-confirm-modal"
        isOpen={confirmModal.open}
        onCloseModal={onConfirmModalClose}
        descriptionText={confirmModal.msg}
        needConfirmation={true}
        onConfirm={() => {
          onConfirm(confirmModal.type);
        }}
      />

      {/* Err/Success Modal */}
      <ModalComponent
        idName="schdule-modal"
        isOpen={infoModal.open}
        onCloseModal={onInfoModalClose}
        descriptionText={
          infoModal?.translated ? infoModal.msg : t(infoModal.msg)
        }
      />

      {isEdit && (
        <div className="bodyInner reset-css-for-bodyInner">
          <div className="scheduleSettingSection">
            <ScheduleSettingEdit
              isOpenFrom="fleetSchd"
              isOpen={isEdit}
              onCloseModal={() => {
                setIsEdit(false);
              }}
              idName="scheduleSettingEditModal"
              taskList={tasks}
              isSchdEnable={taskStatus === schdState.enable ? true : false}
              taskCB={(task, status) => {
                let state = status ? schdState.enable : schdState.disable;
                setTaskStatus(state);
                setTasks([...task]);
                updateLocalStorageData(
                  {
                    templateData: { jobList: task },
                    schdState: state,
                  },
                  siteData.id
                );
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

export default FleetSchedule;
